import { useMemo } from 'react'

import { getPrices } from '@/gen/bff'
import { useSelectedOrgUnitId } from '@/hooks/useSelectedOrgUnitId'
import { useSiteContext } from '@/hooks/useSiteContext'
import { useTenant } from '@/hooks/useTenant'
import { createScopedLoaderCache } from '@/lib/batching/createScopedLoader'
import { createPriceBatchLoader } from './priceBatchLoader'
import { createProductPricesSelect } from './productPricesSelect'

import type { Currency } from '@/config/tenants/types'
import type { LoadedPrice, PriceBatchLoader, PriceLoaderKey } from './priceBatchLoader'

type PriceLoaderScopeArgs = [baseSiteId: string, currency: Currency, locale: string, orgUnitId: string]

// One loader per (site, currency, locale, org): a soft org switch (no reload — see
// .claude/memory/project/commerce-flows/org-switch-soft-reload.md) can leave a load in flight when the org changes,
// so a shared loader could resolve it against the wrong organisation's batch and header.
const getScopedPriceLoader = createScopedLoaderCache<PriceLoaderScopeArgs, PriceLoaderKey, LoadedPrice>(
  (baseSiteId, currency, locale, orgUnitId) => `${baseSiteId}::${currency}::${locale}::${orgUnitId}`,
  (baseSiteId, currency, locale, orgUnitId) => {
    const select = createProductPricesSelect(baseSiteId, currency, locale)

    return createPriceBatchLoader({
      fetchPrices: async (products) =>
        select(
          await getPrices({
            body: { products },
            headers: { 'X-OrgUnit': orgUnitId },
          })
        ),
    })
  }
)

/**
 * Builds the shared, batched price loader. Read it from PriceContextProvider and share it via context —
 * a per-component loader would not coalesce loads across products into bulk requests.
 */
export const usePriceLoader = (): PriceBatchLoader => {
  const { getBaseSiteId } = useSiteContext()
  const tenant = useTenant()
  const orgUnitId = useSelectedOrgUnitId()
  const baseSiteId = getBaseSiteId()

  return useMemo(
    () => getScopedPriceLoader(baseSiteId, tenant.currency, tenant.locale, orgUnitId),
    [baseSiteId, tenant.currency, tenant.locale, orgUnitId]
  )
}
